import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AccountService } from './account.service';
import { AppkitService } from './appkit.service';
import { CommonService } from './common.service';
import { LoggerService } from './logger.service';
import { AuthResponse } from '../models/auth.models';

export interface WalletAuthExtraParams {
    first_name?: string;
    last_name?: string;
    email?: string;
    is_policy_accepted?: boolean;
    user_register_type?: number;
}

/**
 * Coordinates the self-custody wallet auth flow (challenge → sign → verify)
 * across every component that initiates it.
 *
 * Two layers of guards prevent the "Invalid or expired nonce" error that
 * surfaces when multiple flows race against each other:
 *
 *   1. Per-tab in-flight flag — stops the same tab from racing itself when
 *      walletState$ emits multiple times in quick succession (chain switch,
 *      reconnect, account change).
 *   2. Cross-tab Web Locks API — stops multiple browser tabs from each
 *      requesting a fresh challenge for the same wallet. Without this, the
 *      backend's latest-wins nonce policy invalidates earlier nonces and
 *      MetaMask queues stale signature requests that all fail once signed.
 *
 * The method returns `null` when the flow is skipped (another tab or another
 * in-flight call is handling auth). Errors from `signMessage` (user rejection)
 * or from the backend propagate to the caller so existing error-handling
 * logic stays unchanged.
 */
@Injectable({ providedIn: 'root' })
export class WalletAuthCoordinatorService {
    private isAuthInFlight = false;
    private static readonly CROSS_TAB_LOCK_NAME = 'rw-wallet-auth-flow';

    constructor(
        private readonly accountService: AccountService,
        private readonly appkitService: AppkitService,
        private readonly commonService: CommonService,
        private readonly logger: LoggerService
    ) { }

    async runWalletAuthChallenge(
        walletAddress: string,
        extraParams: WalletAuthExtraParams = {}
    ): Promise<AuthResponse | null> {
        if (this.isAuthInFlight) {
            this.logger.debug('Wallet auth already in progress in this tab — skipping duplicate trigger');
            return null;
        }

        const run = () => this.performChallengeSignVerify(walletAddress, extraParams);

        if (this.supportsWebLocks()) {
            let result: AuthResponse | null = null;
            let lockAcquired = false;
            let lockCallbackError: unknown = null;

            try {
                await (navigator as any).locks.request(
                    WalletAuthCoordinatorService.CROSS_TAB_LOCK_NAME,
                    { ifAvailable: true },
                    async (lock: unknown) => {
                        if (!lock) {
                            this.logger.debug('Wallet auth running in another tab — skipping');
                            return;
                        }
                        lockAcquired = true;
                        try {
                            result = await run();
                        } catch (err) {
                            lockCallbackError = err;
                        }
                    }
                );
            } catch (lockErr) {
                this.logger.warn('Web Locks API failed, falling back to in-flight-only guard', lockErr);
                return await run();
            }

            if (lockCallbackError) {
                throw lockCallbackError;
            }

            return lockAcquired ? result : null;
        }

        return await run();
    }

    private async performChallengeSignVerify(
        walletAddress: string,
        extraParams: WalletAuthExtraParams
    ): Promise<AuthResponse> {
        this.isAuthInFlight = true;
        try {
            const challengeRes = await firstValueFrom(this.accountService.walletChallenge(walletAddress));
            const { nonce, message } = challengeRes.data;
            const signature = await this.appkitService.signMessage(message);
            const verifyRes = await firstValueFrom(this.accountService.walletVerify({
                wallet_address: walletAddress,
                signature,
                nonce,
                last_login_os: this.commonService.getUserOS(),
                last_login_browser: this.commonService.getUserBrowser(),
                ...extraParams
            }));
            return verifyRes;
        } finally {
            this.isAuthInFlight = false;
        }
    }

    private supportsWebLocks(): boolean {
        return typeof navigator !== 'undefined'
            && typeof (navigator as any).locks?.request === 'function';
    }
}
